import { useState } from 'react';
import { useApi } from '../../hooks/useApi.js';
import { usePagedList } from '../../hooks/usePagedList.js';
import SortableTh from '../../components/ui/SortableTh.jsx';
import Pagination from '../../components/ui/Pagination.jsx';
import { useToast } from '../../context/ToastContext.jsx';
import { adminGetUsers, adminAssignRole, adminSetUserLevel } from '../../api/adminUsersApi.js';
import { adminGetRoles } from '../../api/adminRolesApi.js';
import { getMembershipLevels } from '../../api/membershipLevelsApi.js';

function AdminUsersPage() {
  const { showToast } = useToast();
  const [edits, setEdits] = useState({});      // { [userId]: 選到的角色代號 }
  const [savingId, setSavingId] = useState(null);
  const [levelSavingId, setLevelSavingId] = useState(null);

  const list = usePagedList(p => adminGetUsers(p), { limit: 10, initialSort: 'createdAt,desc' });
  const { items: users, total, totalPages, page, setPage, sort, toggleSort, loading: usersLoading } = list;
  const { data: roles = [], loading: rolesLoading } = useApi(() => adminGetRoles(), []);
  const { data: levels = [] } = useApi(() => getMembershipLevels(), []);

  const rolesByName = Object.fromEntries(roles.map(r => [r.name, r]));
  const levelName = (lv) => levels.find(l => l.level === lv)?.name ?? `Lv.${lv}`;

  const handleSave = async (user) => {
    const next = edits[user.id];
    if (!next || next === user.role) return;
    setSavingId(user.id);
    try {
      await adminAssignRole(user.id, next);
      showToast(`已將 ${user.username} 指派為「${rolesByName[next]?.displayName || next}」`, 'success');
      setEdits({});
      list.refetch();
    } catch (err) {
      showToast(`指派失敗：${err.message}`, 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handleLevelChange = async (user, nextLevel) => {
    const lv = Number(nextLevel);
    if (lv === user.level) return;
    setLevelSavingId(user.id);
    try {
      await adminSetUserLevel(user.id, lv);
      showToast(`已將 ${user.username} 設為「${levelName(lv)}」`, 'success');
      list.refetch();
    } catch (err) {
      showToast(`調整等級失敗：${err.message}`, 'error');
    } finally {
      setLevelSavingId(null);
    }
  };

  const loading = usersLoading || rolesLoading;

  return (
    <>
      <div className="admin-page-head">
        <h1>使用者管理<small>USERS · {total} 人</small></h1>
      </div>

      <div className="admin-card" style={{ marginBottom: 16 }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 13, lineHeight: 1.7 }}>
          指派角色給使用者。角色決定他能用哪些後台功能；要新增角色或調整角色權限，請到
          <b style={{ color: 'var(--navy)' }}>「角色權限」</b>。
        </p>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <SortableTh field="username" sort={sort} onSort={toggleSort} style={{ width: 160 }}>使用者</SortableTh>
              <SortableTh field="email" sort={sort} onSort={toggleSort}>Email</SortableTh>
              <SortableTh field="role" sort={sort} onSort={toggleSort} style={{ width: 180 }}>角色</SortableTh>
              <th style={{ width: 130 }}>會員等級</th>
              <th style={{ width: 120 }}>邀請人</th>
              <th style={{ width: 100 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>載入中…</td></tr>
              : users.length === 0
                ? <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>尚無使用者</td></tr>
                : users.map(u => {
                  const selected = edits[u.id] ?? u.role;
                  const changed = selected !== u.role;
                  return (
                    <tr key={u.id}>
                      <td style={{ font: '700 14px "Noto Sans TC"', color: 'var(--navy)' }}>{u.username}</td>
                      <td style={{ fontSize: 13 }}>{u.email}</td>
                      <td>
                        <select
                          className="admin-select"
                          value={selected}
                          onChange={e => setEdits(m => ({ ...m, [u.id]: e.target.value }))}
                        >
                          {/* 目前角色若不在角色清單中（例如已被刪），仍保留一個選項避免空白 */}
                          {!rolesByName[u.role] && <option value={u.role}>{u.role}</option>}
                          {roles.map(r => (
                            <option key={r.name} value={r.name}>{r.displayName}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="admin-select"
                          value={u.level ?? 0}
                          disabled={levelSavingId === u.id}
                          onChange={e => handleLevelChange(u, e.target.value)}
                        >
                          {/* 目前等級若不在清單（被刪），保留一個選項避免空白 */}
                          {!levels.some(l => l.level === (u.level ?? 0)) && (
                            <option value={u.level ?? 0}>{levelName(u.level ?? 0)}</option>
                          )}
                          {levels.map(l => (
                            <option key={l.id} value={l.level}>{l.name}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{u.referredByName || '—'}</td>
                      <td>
                        <button
                          className="admin-btn primary sm"
                          disabled={!changed || savingId === u.id}
                          onClick={() => handleSave(u)}
                        >
                          {savingId === u.id ? '儲存中…' : '儲存'}
                        </button>
                      </td>
                    </tr>
                  );
                })
            }
          </tbody>
        </table>
      </div>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </>
  );
}

export default AdminUsersPage;
